import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { LanguagePicker } from "../components/LanguagePicker";
import { api, ApiError } from "../lib/api";

/**
 * The other end of an invite link.
 *
 * Readable signed out, so that someone who has never used the site can see which
 * team they are being asked to join before handing over a Steam login.
 */
export function JoinTeamPage() {
  const { t } = useTranslation();
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const me = useQuery({
    queryKey: ["me"],
    queryFn: api.me,
    retry: (count, error) =>
      !(error instanceof ApiError && error.status === 401) && count < 2,
  });

  const invite = useQuery({
    queryKey: ["invite", token],
    queryFn: () => api.previewInvite(token ?? ""),
    enabled: Boolean(token),
    retry: false,
  });

  const join = useMutation({
    mutationFn: () => api.acceptInvite(token ?? ""),
    onSuccess: async (team) => {
      await queryClient.invalidateQueries({ queryKey: ["teams"] });
      navigate(`/teams/${team.id}`);
    },
  });

  const signedIn = me.isSuccess;

  return (
    <div className="flex min-h-screen items-center justify-center bg-zinc-950 px-6">
      <div className="fixed right-6 top-6">
        <LanguagePicker />
      </div>

      <div className="w-full max-w-md text-center">
        {invite.isPending ? (
          <p className="text-sm text-zinc-500">{t("join.checking")}</p>
        ) : invite.isError || !invite.data ? (
          <>
            <h1 className="text-2xl font-semibold tracking-tight">
              {t("join.invalidTitle")}
            </h1>
            <p className="mt-3 text-sm text-zinc-400">{t("join.invalidBody")}</p>
          </>
        ) : (
          <>
            <p className="text-sm uppercase tracking-wider text-zinc-500">
              {t("join.invitedTo")}
            </p>
            <h1 className="mt-2 text-3xl font-semibold tracking-tight">
              {invite.data.team_name}
            </h1>
            <p className="mt-3 text-sm text-zinc-400">
              {t("join.body", { count: invite.data.member_count })}
            </p>

            {invite.data.already_member ? (
              <button
                type="button"
                onClick={() => navigate(`/teams/${invite.data.team_id}`)}
                className="mt-8 w-full rounded-lg border border-zinc-700 px-4 py-3 text-sm text-zinc-300 transition-colors hover:border-zinc-500"
              >
                {t("join.already")}
              </button>
            ) : signedIn ? (
              <button
                type="button"
                onClick={() => join.mutate()}
                disabled={join.isPending}
                className="mt-8 w-full rounded-lg bg-sky-500 px-4 py-3 font-medium text-zinc-950 transition-colors hover:bg-sky-400 disabled:opacity-50"
              >
                {join.isPending ? t("join.joining") : t("join.join")}
              </button>
            ) : (
              <a
                href={api.steamLoginUrl(`/join/${token}`)}
                className="mt-8 flex w-full items-center justify-center gap-3 rounded-lg bg-sky-500 px-4 py-3 font-medium text-zinc-950 transition-colors hover:bg-sky-400"
              >
                {t("join.steam")}
              </a>
            )}

            {join.isError && (
              <p className="mt-4 text-sm text-red-400">{join.error.message}</p>
            )}
          </>
        )}
      </div>
    </div>
  );
}
